"use strict";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { getLocalStorageItem } from "../utils/storage.js";
import { getPublicStorageUrl, getPublicStorageUrlFromGsPath } from "../utils/storageUrls";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import NotesIcon from "@mui/icons-material/Notes";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SettingsVoiceIcon from "@mui/icons-material/SettingsVoice";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import TuneIcon from "@mui/icons-material/Tune";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Loader from "../Components/Loader";
import { storage } from "../config/firebase";
import {
  CREATE_AUDIOBOOK,
  COPY_CHARACTERS_TO_PROJECT,
  END_TO_END_AUTOMATION,
  URL_UPLOAD,
} from "../ApiEndpoints";
import {
  ELEVENLABS_VOICES,
  CUSTOM_MODEL_VOICES,
  SPECIAL_VOICES,
  AUDIOBOOK_VOICE_NAME_MAP,
} from "../Data/audiobookVoices.js";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { getLanguageName } from "../utils/generalFunctions.js";
import { ToastContainer, toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { postWithFirebaseAuth } from "../utils/firebaseApiClient";
import { languages } from "../Data/languages.js";

const PROJECT_SETUP_STORAGE_PREFIX = "pendingProjectSetup:";
const DIALOGUE_AUDIO_TYPES = new Set(["dialogue", "dialogueMusicEffect"]);
const MUSIC_AUDIO_TYPES = new Set(["music", "musicEffects", "dialogueMusicEffect"]);
const STEM_SEPARATION_OPTIONS = [
  { value: "audioshake", label: "AudioShake (third-party)" },
  { value: "demucs", label: "Demucs (in-house)" },
];
const AUDIO_ELEMENT_BY_TYPE = {
  dialogue: "DX",
  music: "SCORE",
  effect: "FX",
  musicEffects: "ME",
  dialogueMusicEffect: "PM",
};
const AUDIO_CHANNEL_BY_TYPE = {
  right: "R",
  left: "L",
  "multi-channel": "NOCH",
};

const getProjectSetupStorageKey = (setupId) =>
  `${PROJECT_SETUP_STORAGE_PREFIX}${setupId}`;

const readProjectSetupFromSession = (setupId) => {
  if (!setupId) {
    return null;
  }

  try {
    const savedSetup = sessionStorage.getItem(getProjectSetupStorageKey(setupId));
    return savedSetup ? JSON.parse(savedSetup) : null;
  } catch (error) {
    console.error("Failed to read saved project setup:", error);
    return null;
  }
};

const buildProjectCreationSearch = ({ setupId, mode, resumeExistingProject }) => {
  const params = new URLSearchParams();

  if (setupId) {
    params.set("setupId", setupId);
  }

  if (mode) {
    params.set("mode", mode);
  }

  if (resumeExistingProject) {
    params.set("resumeExistingProject", "1");
  }

  const search = params.toString();
  return search ? `?${search}` : "";
};

const uploadChoiceOptions = [
  {
    value: "videoAndAudio",
    title: "Video & Audios",
    description: "Use your own dialogue, music, effects, or mixed audio assets.",
    icon: AudiotrackIcon,
  },
  {
    value: "onlyVideo",
    title: "Only Video",
    description: "Upload video and automatically prepare dialogue and non-vocal tracks.",
    icon: MovieCreationIcon,
  },
];

const videoSourceOptions = [
  { value: "file", title: "Upload File", icon: UploadFileIcon },
  { value: "google-drive", title: "Google Drive", icon: DriveFolderUploadIcon },
];

const inputSx = {
  width: "100%",
  minHeight: 48,
  border: "1px solid rgba(8, 72, 96, 0.16)",
  borderRadius: "16px",
  px: 1.75,
  background: "rgba(255,255,255,0.86)",
  outline: "none",
  font: "inherit",
  color: "#142234",
  transition: "all 160ms ease",
  "&:focus": {
    borderColor: "#00779f",
    boxShadow: "0 0 0 4px rgba(0,119,159,0.10)",
  },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.86)",
    "& fieldset": { borderColor: "rgba(8, 72, 96, 0.16)" },
    "&:hover fieldset": { borderColor: "rgba(0,119,159,0.45)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(0,119,159,0.10)" },
  },
};

const pageSx = {
  minHeight: "calc(100vh - 90px)",
  width: "100%",
  maxWidth: "100vw",
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
  boxSizing: "border-box",
  fontFamily: "var(--font-body)",
  background:
    "radial-gradient(circle at 18% 12%, rgba(0, 149, 199, 0.18), transparent 28%), radial-gradient(circle at 86% 24%, rgba(47, 188, 179, 0.16), transparent 30%), linear-gradient(135deg, #eaf7fa 0%, #e6f3ef 50%, #f7fbfc 100%)",
  backgroundAttachment: "fixed",
  position: "relative",
  overflowX: "hidden",
  "& *": {
    boxSizing: "border-box",
  },
  "& .MuiGrid-item": {
    minWidth: 0,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    opacity: 0.45,
    backgroundImage:
      "linear-gradient(45deg, rgba(255,255,255,0.35) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.35) 75%, transparent 75%, transparent)",
    backgroundSize: "12px 12px",
  },
};

const shellSx = {
  width: "100%",
  maxWidth: "1440px",
  mx: "auto",
  position: "relative",
  zIndex: 1,
  minWidth: 0,
};

const glassCardSx = {
  borderRadius: "32px",
  border: "1px solid rgba(255,255,255,0.72)",
  background: "rgba(255,255,255,0.88)",
  boxShadow: "0 24px 80px rgba(13, 57, 78, 0.14)",
  backdropFilter: "blur(18px)",
  minWidth: 0,
};

const primaryButtonSx = {
  minHeight: 48,
  px: 3,
  borderRadius: "16px",
  fontWeight: 900,
  textTransform: "none",
  background: "linear-gradient(135deg, #00779f 0%, #00a4c7 100%)",
  boxShadow: "0 14px 28px rgba(0, 119, 159, 0.24)",
  "&:hover": {
    background: "linear-gradient(135deg, #00698d 0%, #0094b5 100%)",
    boxShadow: "0 18px 34px rgba(0, 119, 159, 0.28)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.72)",
    background: "#94a3b8",
  },
};

const outlineButtonSx = {
  minHeight: 44,
  borderRadius: "15px",
  textTransform: "none",
  fontWeight: 900,
  borderColor: "rgba(0,119,159,0.28)",
  color: "#00779f",
  "&:hover": {
    borderColor: "#00779f",
    backgroundColor: "rgba(0,119,159,0.08)",
  },
};

const SectionCard = ({ icon: Icon, title, eyebrow, children, action }) => (
  <Paper
    elevation={0}
    sx={{ ...glassCardSx, p: { xs: 2.5, md: 3.5 }, width: "100%", height: "100%" }}
  >
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={2}
      mb={3}
      minWidth={0}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
        {Icon && (
          <Avatar
            sx={{
              width: 42,
              height: 42,
              color: "#00779f",
              bgcolor: "rgba(0, 119, 159, 0.10)",
              borderRadius: "14px",
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Avatar>
        )}
        <Box minWidth={0}>
          {eyebrow && (
            <Typography color="text.secondary" fontSize={12} fontWeight={900} textTransform="uppercase">
              {eyebrow}
            </Typography>
          )}
          <Typography fontSize={{ xs: 20, md: 24 }} fontWeight={950} color="#0e1d2b">
            {title}
          </Typography>
        </Box>
      </Stack>
      {action}
    </Stack>
    {children}
  </Paper>
);

const OptionCard = ({ active, icon: Icon, title, description, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: "100%",
      height: "100%",
      p: 2.3,
      textAlign: "left",
      borderRadius: "22px",
      border: `1px solid ${active ? "#00779f" : "rgba(7, 43, 59, 0.10)"}`,
      background: active
        ? "linear-gradient(135deg, rgba(0,119,159,0.13), rgba(255,255,255,0.94))"
        : "rgba(255,255,255,0.72)",
      boxShadow: active
        ? "0 18px 42px rgba(0, 119, 159, 0.16)"
        : "0 12px 30px rgba(13, 57, 78, 0.08)",
      cursor: "pointer",
      transition: "all 180ms ease",
      display: "flex",
      gap: 1.7,
      alignItems: "flex-start",
      minWidth: 0,
      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: "#00779f",
        boxShadow: "0 18px 42px rgba(0, 119, 159, 0.14)",
      },
    }}
  >
    <Avatar
      sx={{
        mt: 0.25,
        width: 44,
        height: 44,
        color: active ? "#fff" : "#00779f",
        bgcolor: active ? "#00779f" : "rgba(0, 119, 159, 0.10)",
        borderRadius: "16px",
        flexShrink: 0,
      }}
    >
      <Icon fontSize="small" />
    </Avatar>
    <Box flex={1} minWidth={0}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography fontWeight={950} fontSize={17} color="#142234">
          {title}
        </Typography>
        {active && <CheckCircleIcon sx={{ color: "#00779f", fontSize: 20 }} />}
      </Stack>
      {description && (
        <Typography mt={0.75} fontSize={14} lineHeight={1.55} color="#64748b">
          {description}
        </Typography>
      )}
    </Box>
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography fontWeight={900} mb={1} color="#243447">
    {children}
  </Typography>
);

const StatusChip = ({ done, label }) => (
  <Chip
    icon={done ? <CheckCircleIcon /> : undefined}
    label={label}
    sx={{
      fontWeight: 900,
      color: done ? "#047857" : "#64748b",
      bgcolor: done ? "rgba(16,185,129,0.12)" : "rgba(100,116,139,0.10)",
      "& .MuiChip-icon": { color: done ? "#047857" : "#64748b" },
    }}
  />
);
const getDefaultVideoOptions = () => ({
  chosenType: "file",
  drive: { error: false, url: "" },
  dropbox: { error: false, url: "" },
  direct: { error: false, url: "" },
  file: { error: false, uploaded: false, filePath: "", fileName: "" },
});

const sanitizeVideoOptionsForSession = (options = getDefaultVideoOptions()) => {
  const defaultOptions = getDefaultVideoOptions();
  const selectedFile = options.file?.filePath;

  return {
    ...defaultOptions,
    ...options,
    chosenType: ["file", "google-drive"].includes(options.chosenType)
      ? options.chosenType
      : "file",
    drive: { ...defaultOptions.drive, ...(options.drive || {}) },
    dropbox: { ...defaultOptions.dropbox, ...(options.dropbox || {}) },
    direct: { ...defaultOptions.direct, ...(options.direct || {}) },
    file: {
      ...defaultOptions.file,
      ...(options.file || {}),
      filePath: "",
      fileName: selectedFile?.name || options.file?.fileName || "",
    },
  };
};

const getAutomationVideoUrl = (response, sourceUrl) => {
  const videoUrl =
    response?.data?.blob_path ||
    response?.data?.data?.output?.download_path ||
    sourceUrl;

  if (!videoUrl) {
    throw new Error("The automation request did not include a video source");
  }

  return videoUrl;
};

const shouldRunDemucs = (uploadOption, audioAssets) => {
  if (uploadOption === "onlyVideo") {
    return true;
  }

  const hasDialogueAudio = audioAssets.some((audio) =>
    DIALOGUE_AUDIO_TYPES.has(audio.audioType)
  );
  const hasMusicAudio = audioAssets.some((audio) =>
    MUSIC_AUDIO_TYPES.has(audio.audioType)
  );

  return !hasDialogueAudio || !hasMusicAudio;
};


const ProjectCreationDetailsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const setupIdFromRoute =
    location.state?.projectSetupId ||
    location.state?.projectSetup?.setupId ||
    searchParams.get("setupId");
  const savedSetup = readProjectSetupFromSession(setupIdFromRoute);
  const routeSetup = location.state?.projectSetup;
  const projectSetup = savedSetup
    ? {
        ...(routeSetup || {}),
        ...savedSetup,
        detailsState: {
          ...(routeSetup?.detailsState || {}),
          ...(savedSetup?.detailsState || {}),
        },
      }
    : routeSetup;
  const projectSetupId = projectSetup?.setupId || setupIdFromRoute || "";
  const restoredDetails = projectSetup?.detailsState || {};

  const [videoURL, setVideoURL] = useState(restoredDetails.videoURL || null);
  const projectName = projectSetup?.projectName || "";
  const projectFinalLangs = projectSetup?.projectFinalLangs || [];
  const displaySelection = projectSetup?.displaySelection || [];
  const [upLoader, setUpLoader] = useState(false);
  const [videoProgress, setVideoProgress] = useState(restoredDetails.videoProgress || 0);
  const [videoUploading, setVideoUploading] = useState(false);
  const [audioProgress, setAudioProgress] = useState(restoredDetails.audioProgress || 0);

  const [uploadOptions, setUploadOption] = useState(restoredDetails.uploadOptions || "videoAndAudio");
  const [videoOptions, setVideoOptions] = useState({
    ...getDefaultVideoOptions(),
    ...(restoredDetails.videoOptions || {}),
    chosenType: ["file", "google-drive"].includes(
      restoredDetails.videoOptions?.chosenType
    )
      ? restoredDetails.videoOptions.chosenType
      : "file",
    file: {
      ...getDefaultVideoOptions().file,
      ...(restoredDetails.videoOptions?.file || {}),
      filePath: "",
    },
  });

  const [subtitleFiles, setSubtitleFiles] = useState(
    restoredDetails.subtitleFiles || []
  );
  const [showAudioAddingOption, setShowAudioAddingOption] = useState(restoredDetails.showAudioAddingOption || false);
  const [showAddingSrtFileOption, setShowAddingSrtFileOption] = useState(restoredDetails.showAddingSrtFileOption || false);
  const [channelType, setChannelType] = useState(restoredDetails.channelType || "");
  const [audioType, setAudioType] = useState(restoredDetails.audioType || "");
  const [uploadType, setUploadType] = useState(restoredDetails.uploadType || "");
  const [selectUrlFile, setSelectUrlFile] = useState(restoredDetails.selectUrlFile || "");
  const [uploadedAudioTypes, setUploadedAudioTypes] = useState(restoredDetails.uploadedAudioTypes || []);
  const [facesToggle, setFacesToggle] = useState(restoredDetails.facesToggle || false);
  const [runFullWorkflow, setRunFullWorkflow] = useState(
    restoredDetails.runFullWorkflow ?? true
  );
  const [stemSeparationProvider, setStemSeparationProvider] = useState(
    restoredDetails.stemSeparationProvider || "audioshake"
  );

  const initialProjectType =
    searchParams.get("type") === "audiobook" ? "audiobook" : "video";
  const mode = searchParams.get("mode");

  const missingProjectSetup = !projectSetup;
  const projectId =
    projectSetup?.projectId ||
    projectSetup?.id ||
    projectSetup?.docId ||
    projectSetup?.project?.projectId ||
    projectSetup?.project?.id ||
    "";

  const projectType = projectSetup?.projectType || initialProjectType;
  const [selectedVoice, setSelectedVoice] = useState(restoredDetails.selectedVoice || "");
  const [txtFile, setTxtFile] = useState(null);
  const [txtFileMeta, setTxtFileMeta] = useState(restoredDetails.txtFileMeta || null);
  const [txtFileData, setTxtFileData] = useState(restoredDetails.txtFileData || null);
  const [provider, voiceId] = selectedVoice.split(":");
  const selectedSourceProject = projectSetup?.selectedSourceProject || null;

  useEffect(() => {
    if (mode !== "embed" || typeof document === "undefined") {
      return;
    }

    const header = document.getElementById("header");
    const footer = document.getElementById("footer");

    if (header) header.style.display = "none";
    if (footer) footer.style.display = "none";
  }, [mode]);

  const getCurrentProjectSetup = (detailsOverrides = {}) => {
    const currentDetailsState = {
      videoURL,
      videoProgress,
      audioProgress,
      uploadOptions,
      videoOptions: sanitizeVideoOptionsForSession(videoOptions),
      subtitleFiles: subtitleFiles
        .filter((subtitle) => subtitle.objectName)
        .map(({ file, ...subtitle }) => subtitle),
      showAudioAddingOption,
      showAddingSrtFileOption,
      channelType,
      audioType,
      uploadType,
      selectUrlFile: typeof selectUrlFile === "string" ? selectUrlFile : "",
      uploadedAudioTypes,
      facesToggle,
      runFullWorkflow,
      stemSeparationProvider,
      selectedVoice,
      txtFileMeta,
      txtFileData,
      ...detailsOverrides,
    };

    return {
      ...(projectSetup || {}),
      setupId: projectSetupId,
      projectId,
      projectName,
      projectFinalLangs,
      displaySelection,
      projectType,
      selectedSourceProject,
      detailsState: currentDetailsState,
    };
  };

  const persistProjectSetup = (detailsOverrides = {}) => {
    if (!projectSetupId) {
      return projectSetup;
    }

    const nextProjectSetup = getCurrentProjectSetup(detailsOverrides);
    sessionStorage.setItem(
      getProjectSetupStorageKey(projectSetupId),
      JSON.stringify(nextProjectSetup)
    );

    return nextProjectSetup;
  };

  const handleSubmitCreation = async () => {
    if (projectType === "audiobook") {
      if (!projectName) {
        toast.error("Project name is required");
        return;
      }
      if (!txtFileData) {
        toast.error("Please upload a text file");
        return;
      }
      if (!selectedVoice) {
        toast.error("Please select a voice");
        return;
      }
      if (projectFinalLangs.length === 0) {
        toast.error("Please select at least one target language");
        return;
      }

      setUpLoader(true);

      try {
        const userInfo = getLocalStorageItem("userInfo", {});
        if (!userInfo || !userInfo.id) {
          toast.error("User authentication required");
          return;
        }

        const textFileName = txtFile?.name || txtFileMeta?.name || "audiobook_text.txt";
        const versionFileName = `${textFileName.split(".")[0]}_${uuidv4()}.txt`;

        const response = await axios.post(CREATE_AUDIOBOOK, {
          projectName,
          finalLanguages: projectFinalLangs,
          voiceName: getVoiceName(selectedVoice),
          voiceUid: voiceId,
          voiceProvider: provider,
          voiceVersion: "1.0",
          emotion: "neutral",
          employees: displaySelection,
          versionFileName,
          textContent: txtFileData,
          userId: userInfo.id,
        });

        if (response.data.success) {
          toast.success("Audiobook project created successfully");

          if (projectSetupId) {
            sessionStorage.removeItem(getProjectSetupStorageKey(projectSetupId));
          }

          navigate("/adminlanding");
        }
      } catch (error) {
        console.error("Error creating audiobook project:", error);
        toast.error(error.response?.data?.message || "Error creating audiobook project");
      } finally {
        setUpLoader(false);
      }
      return;
    }

    if (!projectId) {
      toast.error("Missing project id. Please go back to setup and create the project first.");
      return;
    }

    // const officialDialogueAudioUrl = uploadedAudioTypes.find(
    //   (audio) => audio.audioType === "dialogue"
    // );

    // if (uploadOptions === "videoAndAudio" && !officialDialogueAudioUrl) {
    //   toast.error("Project can't be completed without any dialogue audio!!");
    //   return;
    // }

    if (!videoURL) {
      toast.error("Please upload the video source first");
      return;
    }

    setUpLoader(true);

    try {
      const subtitleAssets = await prepareSubtitleAssets();
      const automationResponse = await postWithFirebaseAuth(END_TO_END_AUTOMATION, {
        projectId,
        projectName,
        url: videoURL,
        collectionAssets: subtitleAssets,
        demucs: shouldRunDemucs(uploadOptions, uploadedAudioTypes),
        isDrive: videoOptions.chosenType !== "dropbox",
        runFullWorkflow,
        runLipSync: facesToggle && runFullWorkflow,
        stemSeparationProvider,
      });
      const automationVideoUrl = getAutomationVideoUrl(automationResponse, videoURL);
      setVideoURL(automationVideoUrl);

      if (selectedSourceProject && selectedSourceProject.id) {
        try {
          const copyResponse = await postWithFirebaseAuth(COPY_CHARACTERS_TO_PROJECT, {
            projectId,
            projectIds: [selectedSourceProject.id],
          });

          console.log("Characters copied:", copyResponse.data);

          if (copyResponse.data.added > 0) {
            toast.success(`${copyResponse.data.added} characters copied from "${selectedSourceProject.name}"`);
          }
        } catch (copyError) {
          console.error("Error copying characters:", copyError);
        }
      }

      toast.success("Project details completed");

      if (projectSetupId) {
        sessionStorage.removeItem(getProjectSetupStorageKey(projectSetupId));
      }

      navigate("/adminlanding");
    } catch (error) {
      console.log(error);
      toast.error("Error while completing project details");
    } finally {
      setUpLoader(false);
    }
  };

  const addSubtitleFiles = (files) => {
    const selectedFiles = Array.from(files || []);
    const invalidFile = selectedFiles.find((file) => !/\.srt$/i.test(file.name));
    if (invalidFile) {
      toast.error("Subtitle files must use the .srt extension");
      return;
    }

    setSubtitleFiles((current) => {
      let hasOriginal = current.some((subtitle) => subtitle.role === "original");
      const additions = selectedFiles.map((file) => {
        const role = hasOriginal ? "translated" : "original";
        hasOriginal = true;
        return {
          id: uuidv4(),
          file,
          fileName: file.name,
          role,
          languageCode: "",
          progress: 0,
          objectName: "",
        };
      });
      return [...current, ...additions];
    });
  };

  const updateSubtitleFile = (id, updates) => {
    setSubtitleFiles((current) =>
      current.map((subtitle) =>
        subtitle.id === id ? { ...subtitle, ...updates } : subtitle
      )
    );
  };

  const removeSubtitleFile = (id) => {
    setSubtitleFiles((current) =>
      current.filter((subtitle) => subtitle.id !== id)
    );
  };

  const uploadSubtitleFile = (subtitle) => {
    if (subtitle.objectName) {
      return Promise.resolve(subtitle);
    }
    if (!subtitle.file) {
      return Promise.reject(new Error(`Choose ${subtitle.fileName || "the subtitle"} again`));
    }

    const safeFileName = subtitle.file.name.replace(/[^A-Za-z0-9._-]+/g, "_");
    const objectName =
      `projects/${projectId}/uploads/subtitles/${subtitle.id}/${safeFileName}`;
    const uploadTask = uploadBytesResumable(ref(storage, objectName), subtitle.file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = snapshot.totalBytes
            ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            : 0;
          updateSubtitleFile(subtitle.id, { progress });
        },
        reject,
        () => {
          const uploaded = { ...subtitle, objectName, progress: 100 };
          updateSubtitleFile(subtitle.id, uploaded);
          resolve(uploaded);
        }
      );
    });
  };

  const prepareSubtitleAssets = async () => {
    const originalFiles = subtitleFiles.filter(
      (subtitle) => subtitle.role === "original"
    );
    if (originalFiles.length > 1) {
      throw new Error("Only one original subtitle file is allowed");
    }

    const translatedLanguages = new Set();
    for (const subtitle of subtitleFiles) {
      if (subtitle.role === "translated" && !subtitle.languageCode) {
        throw new Error(`Select a language for ${subtitle.fileName}`);
      }
      if (
        subtitle.role === "translated" &&
        translatedLanguages.has(subtitle.languageCode)
      ) {
        throw new Error(
          `Only one translated subtitle is allowed for ${subtitle.languageCode}`
        );
      }
      if (subtitle.role === "translated") {
        translatedLanguages.add(subtitle.languageCode);
      }
    }

    const uploaded = await Promise.all(subtitleFiles.map(uploadSubtitleFile));
    return uploaded.map((subtitle) => ({
      mediaType: "subtitle",
      subtitleRole: subtitle.role,
      ...(subtitle.languageCode ? { languageCode: subtitle.languageCode } : {}),
      originalFileName: subtitle.fileName,
      storage: { objectName: subtitle.objectName },
    }));
  };

  const handleVideoUpload = () => {
    return new Promise((resolve, reject) => {
      if (!projectName) {
        toast.error("you should fill the project name");
        resolve(null);
        return;
      }

      if (!videoOptions.file.filePath) {
        toast.error("you should choose a video file first");
        resolve(null);
        return;
      }

      toast.info("Video is being uploaded wait for it");

      console.log(`videoOptions.chosenType: ${videoOptions.chosenType}`);

      const videoFile = videoOptions.file.filePath;
      const fileName = `${projectName}/${videoFile.name}`;
      const storageRef = ref(storage, fileName);
      const uploadTask = uploadBytesResumable(storageRef, videoFile);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setVideoProgress(progress);
          switch (snapshot.state) {
            case "paused":
              console.log("Upload is pause");
              break;
            case "running":
              console.log("Upload is Running");
              break;
            default:
              break;
          }
        },
        (error) => {
          console.log(error);
          toast.error("Error while uploading the video");
          reject(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((URL) => {
            try {
              setVideoURL(URL);
              toast.success("Video has been uploaded successfully");

              setVideoOptions((prevMode) => ({
                ...prevMode,
                file: {
                  ...prevMode.file,
                  filePath: "",
                  uploaded: true,
                },
              }));

              resolve(URL);
            } catch (error) {
              console.log(error);
              toast.error("Error while uploading the video");
              reject(error);
            }
          });
        }
      );
    });
  };

  const handleVideoUploadDrive = async () => {
    console.log(`videoOptions.chosenType: ${videoOptions.chosenType}`);
    toast.info("Video is getting ready to be uploaded wait a bit!");
    const videoDriveUrl = videoOptions.drive.url;

    if (!videoDriveUrl) {
      toast.error("you should fill the video drive url first");
      return null;
    }

    if (!projectName) {
      toast.error("you should fill the project name");
      return null;
    }

    try {
      setVideoURL(videoDriveUrl);
      toast.success("Google Drive video source is ready");
      return videoDriveUrl;
    } catch (error) {
      console.log(error);
      toast.error("Error while uploading the video");
      throw error;
    }
  };

  const handleVideoUploadDropbox = async () => {
    console.log(`videoOptions.chosenType: ${videoOptions.chosenType}`);
    toast.info("Video is getting ready to be uploaded wait a bit!");
    const videoDropboxUrl = videoOptions.dropbox.url;

    if (!videoDropboxUrl) {
      toast.error("you should fill the video dropbox url first");
      return null;
    }

    if (!projectName) {
      toast.error("you should fill the project name");
      return null;
    }

    try {
      setVideoURL(videoDropboxUrl);
      toast.success("Dropbox video source is ready");
      return videoDropboxUrl;
    } catch (error) {
      console.log(error);
      toast.error("Error while uploading the video");
      throw error;
    }
  };

  const handleDirectAudioUpload = () => {
    if (!projectName) {
      toast.error("you should fill the project name");
      return;
    }
    toast.info("file is being uploaded wait for it");

    const audioFile = selectUrlFile;
    const fileName = `${projectName}/${projectName}_${audioType}_${channelType}.wav`;
    const storageRef = ref(storage, fileName);
    const uploadTask = uploadBytesResumable(storageRef, audioFile);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setAudioProgress(progress);
        switch (snapshot.state) {
          case "paused":
            console.log("Upload is pause");
            break;
          case "running":
            console.log("Upload is Running");
            break;
          default:
            break;
        }
      },
      (error) => {
        console.log(error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then(async (URL) => {
          try {
            const audioUrl = await uploadAudioFromUrl(URL, "directUpload");
            completeAudioUpload(audioUrl);
          } catch (error) {
            console.error("Error registering uploaded audio:", error);
            toast.error("Error while registering the uploaded audio");
          }
        });
      }
    );
  };

  const uploadAudioFromUrl = async (sourceUrl, sourceUploadType) => {
    const response = await postWithFirebaseAuth(URL_UPLOAD, {
      project_id: projectId,
      url: sourceUrl,
      audio_type: audioType,
      channel_type: channelType,
      upload_type: sourceUploadType,
      asset: {
        type: "audio",
        seq: "LP",
        fps: "2398",
        fmt: "LORO",
        elem: AUDIO_ELEMENT_BY_TYPE[audioType] || "PM",
        ch: AUDIO_CHANNEL_BY_TYPE[channelType] || "NOCH",
      },
    });

    return getPublicStorageUrlFromGsPath(response.data.gs_src);
  };

  const completeAudioUpload = (audioUrl) => {
    toast.success(`${audioType} file has been uploaded successfully`);
    setUploadedAudioTypes((prev) => [
      ...prev,
      {
        channelType,
        audioType,
        uploadType,
        url: audioUrl,
      },
    ]);
    setChannelType("");
    setAudioType("");
    setUploadType("");
    setSelectUrlFile("");
    setAudioProgress(0);
    setShowAudioAddingOption(false);
  };

  const handleUrlAudioUpload = async (uploadMethod) => {
    if (!selectUrlFile) {
      toast.error("you should fill the url box");
      return;
    }

    if (!projectName) {
      toast.error("you should fill the project name");
      return;
    }
    if (!channelType) {
      toast.error("choose a channel type");
      return;
    }

    toast.info("Audio url is uploading, wait for it");

    try {
      const sourceUrl =
        uploadMethod === "dropbox"
          ? selectUrlFile.replace(
              /([?&]dl=)0(?=&|$)/,
              (match, prefix) => `${prefix}1`
            )
          : selectUrlFile;
      const audioUrl = await uploadAudioFromUrl(sourceUrl, uploadMethod);
      completeAudioUpload(audioUrl);
      setUpLoader(false);
    } catch (error) {
      console.log(error);
      toast.error("Error while uploading Audio dialogue");
      setUpLoader(false);
    }
  };

  function isValidUrl(url) {
    if (!channelType) {
      return false;
    }
    if (!audioType) {
      return false;
    }

    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    return urlRegex.test(url);
  }

  function isValidUrlForVideo(url) {
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    return urlRegex.test(url);
  }

  const handleRemoveUploadedFile = (url) => {
    const newItems = uploadedAudioTypes.filter((item) => item.url !== url);
    setUploadedAudioTypes(newItems);
  };

  const handleTxtFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setTxtFile(file);
    setTxtFileMeta({
      name: file.name,
      size: file.size,
      type: file.type,
    });

    const reader = new FileReader();
    reader.onload = (e) => {
      setTxtFileData(e.target.result);
      toast.success("Text file loaded successfully");
    };
    reader.onerror = () => {
      toast.error("Error reading text file");
    };
    reader.readAsText(file);
  };

  const handleDirectUrlUpload = async () => {
    toast.info("Processing direct URL video…");

    const { url } = videoOptions.direct;
    if (!isValidUrlForVideo(url)) {
      toast.error("Bad URL");
      return null;
    }

    try {
      setVideoURL(url);
      toast.success("Direct video source is ready");
      return url;
    } catch (error) {
      console.log(error);
      toast.error("Error while uploading the video");
      throw error;
    }
  };

  const canUploadSelectedVideoSource = () => {
    if (videoUploading) {
      return false;
    }

    if (videoOptions.chosenType === "file") {
      return Boolean(videoOptions.file.filePath);
    }

    if (videoOptions.chosenType === "google-drive") {
      return isValidUrlForVideo(videoOptions.drive.url);
    }

    if (videoOptions.chosenType === "dropbox") {
      return isValidUrlForVideo(videoOptions.dropbox.url);
    }

    if (videoOptions.chosenType === "direct") {
      return isValidUrlForVideo(videoOptions.direct.url);
    }

    return false;
  };

  const handleVideoSourceUpload = async () => {
    if (!projectId) {
      toast.error("Missing project id. Please go back to setup and create the project first.");
      return;
    }

    setVideoUploading(true);

    try {
      if (videoOptions.chosenType === "file") {
        await handleVideoUpload();
      } else if (videoOptions.chosenType === "google-drive") {
        await handleVideoUploadDrive();
      } else if (videoOptions.chosenType === "dropbox") {
        await handleVideoUploadDropbox();
      } else if (videoOptions.chosenType === "direct") {
        await handleDirectUrlUpload();
      }
    } finally {
      setVideoUploading(false);
    }
  };

  const resetVideoSource = (sourceType) => {
    setVideoOptions({
      chosenType: sourceType,
      drive: { error: false, url: "" },
      dropbox: { error: false, url: "" },
      direct: { error: false, url: "" },
      file: { error: false, uploaded: false, filePath: "" },
    });
    setVideoURL(null);
    setVideoProgress(0);
  };

  const completionItems = [
    { label: "Video source", done: Boolean(videoURL) || projectType === "audiobook" },
    { label: "Dialogue audio", done: projectType === "audiobook" || uploadOptions === "onlyVideo" || uploadedAudioTypes.some((audio) => audio.audioType === "dialogue") },
    { label: projectType === "audiobook" ? "Text file" : "SRT optional", done: projectType === "audiobook" ? Boolean(txtFileData) : subtitleFiles.length > 0 },
    { label: projectType === "audiobook" ? "Voice" : "Lip sync optional", done: projectType === "audiobook" ? Boolean(selectedVoice) : facesToggle },
  ];

  if (missingProjectSetup) {
    return (
      <Box sx={pageSx}>
        <ToastContainer position="top-center" />
        <Box sx={shellSx}>
          <Paper elevation={0} sx={{ ...glassCardSx, p: { xs: 3, md: 5 }, textAlign: "center" }}>
            <Avatar sx={{ mx: "auto", mb: 2, width: 58, height: 58, borderRadius: "18px", color: "#00779f", bgcolor: "rgba(0,119,159,0.10)" }}>
              <AutoAwesomeIcon />
            </Avatar>
            <Typography fontSize={{ xs: 28, md: 38 }} fontWeight={950} color="#0d1724">
              Complete Project Details
            </Typography>
            <Typography mt={1} mb={3} color="#64748b">
              Please complete the project setup screen first.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/createproject")}
              sx={primaryButtonSx}
            >
              Go to Setup
            </Button>
          </Paper>
        </Box>
      </Box>
    );
  }

  const renderVideoSourceInput = () => {
    if (videoOptions.chosenType === "file") {
      return (
        <Stack spacing={1.5}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            sx={outlineButtonSx}
          >
            {videoOptions.file.filePath?.name || videoOptions.file.fileName || "Choose video file"}
            <input
              hidden
              type="file"
              onChange={(e) => {
                setVideoOptions((prevMode) => ({
                  ...prevMode,
                  file: {
                    ...prevMode.file,
                    filePath: e.target.files[0],
                  },
                }));
                setVideoURL(null);
                setVideoProgress(0);
              }}
            />
          </Button>
          <Typography color="#64748b" fontSize={13.5}>
            Upload the master video file. Progress appears after upload starts.
          </Typography>
        </Stack>
      );
    }

    if (videoOptions.chosenType === "dropbox") {
      return (
        <Box component="input"
          sx={inputSx}
          type="text"
          placeholder="Paste Dropbox URL"
          value={videoOptions.dropbox.url}
          onChange={(e) => {
            setVideoOptions((prevMode) => ({
              ...prevMode,
              dropbox: {
                ...prevMode.dropbox,
                url: e.target.value,
                error: isValidUrlForVideo(e.target.value),
              },
            }));
          }}
        />
      );
    }

    if (videoOptions.chosenType === "google-drive") {
      return (
        <Box component="input"
          sx={inputSx}
          type="text"
          placeholder="Paste Google Drive URL"
          value={videoOptions.drive.url}
          onChange={(e) => {
            setVideoOptions((prevMode) => ({
              ...prevMode,
              drive: {
                ...prevMode.drive,
                url: e.target.value,
                error: isValidUrlForVideo(e.target.value),
              },
            }));
          }}
        />
      );
    }

    return (
      <Box component="input"
        sx={inputSx}
        type="text"
        placeholder="Paste direct video URL (mp4/mov)"
        value={videoOptions.direct.url}
        onChange={(e) => {
          setVideoOptions((prevMode) => ({
            ...prevMode,
            direct: {
              ...prevMode.direct,
              url: e.target.value,
              error: isValidUrlForVideo(e.target.value),
            },
          }));
        }}
      />
    );
  };

  const renderAudioUploadInput = () => {
    if (uploadType === "directUpload") {
      return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={outlineButtonSx}>
            {selectUrlFile?.name || "Choose audio file"}
            <input
              hidden
              type="file"
              onChange={(e) => {
                setSelectUrlFile(e.target.files[0]);
              }}
            />
          </Button>
          <Button
            disabled={!selectUrlFile?.type || !channelType || audioType === ""}
            onClick={handleDirectAudioUpload}
            variant="contained"
            sx={primaryButtonSx}
          >
            Upload
          </Button>
        </Stack>
      );
    }

    if (["googleDrive", "dropbox", "sharePoint", "azureBlob"].includes(uploadType)) {
      const labels = {
        googleDrive: "Google Drive URL",
        dropbox: "Dropbox URL",
        sharePoint: "SharePoint URL",
        azureBlob: "Azure Blob URL",
      };

      return (
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
          <Box
            component="input"
            sx={inputSx}
            type="text"
            placeholder={labels[uploadType]}
            value={typeof selectUrlFile === "string" ? selectUrlFile : ""}
            onChange={(e) => {
              setSelectUrlFile(e.target.value);
            }}
          />
          <Button
            disabled={!isValidUrl(selectUrlFile)}
            onClick={() => handleUrlAudioUpload(uploadType)}
            variant="contained"
            sx={primaryButtonSx}
          >
            Upload
          </Button>
        </Stack>
      );
    }

    return (
      <Typography color="#64748b" fontSize={14}>
        Choose an upload method to continue.
      </Typography>
    );
  };

  return (
    <Box sx={pageSx}>
      <Loader Load={upLoader} />
      <ToastContainer position="top-center" />

      <Box sx={shellSx}>
        <Stack alignItems="center" textAlign="center" spacing={1.5} mb={4}>
          <Avatar
            sx={{
              width: 58,
              height: 58,
              borderRadius: "18px",
              color: "#00779f",
              bgcolor: "rgba(255,255,255,0.88)",
              boxShadow: "0 16px 45px rgba(0, 119, 159, 0.16)",
            }}
          >
            <AutoAwesomeIcon />
          </Avatar>
          <Chip
            label="Step 2 of 2 · Source details"
            sx={{
              fontWeight: 900,
              color: "#00779f",
              bgcolor: "rgba(255,255,255,0.78)",
              border: "1px solid rgba(0,119,159,0.12)",
            }}
          />
          <Typography fontSize={{ xs: 34, md: 46 }} fontWeight={950} letterSpacing={-1.5} color="#0d1724">
            Complete Project Details
          </Typography>
          <Typography maxWidth={720} color="#5f7188" fontSize={{ xs: 15, md: 17 }}>
            Upload the right sources, review the setup, and finish the workflow with fewer decisions per section.
          </Typography>
        </Stack>

        <Stack spacing={3.5} minWidth={0}>
          <Paper elevation={0} sx={{ ...glassCardSx, p: { xs: 2.5, md: 3.5 } }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
              <Box>
                <Typography color="text.secondary" fontSize={12} fontWeight={900} textTransform="uppercase">
                  Project snapshot
                </Typography>
                <Typography fontSize={{ xs: 24, md: 30 }} fontWeight={950} color="#0d1724">
                  {projectName}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1.5}>
                  <Chip icon={<MovieCreationIcon />} label={projectType} sx={{ fontWeight: 800 }} />
                  <Chip label={`Targets: ${projectFinalLangs.map(getLanguageName).join(", ") || "None"}`} sx={{ fontWeight: 800 }} />
                  <Chip label={`${displaySelection.length} member${displaySelection.length === 1 ? "" : "s"}`} sx={{ fontWeight: 800 }} />
                </Stack>
              </Box>

              <Stack spacing={1} alignItems={{ xs: "stretch", md: "flex-end" }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => {
                    const latestProjectSetup = persistProjectSetup();
                    navigate(
                      {
                        pathname: "/createproject",
                        search: buildProjectCreationSearch({
                          setupId: projectSetupId,
                          mode,
                          resumeExistingProject: true,
                        }),
                      },
                      {
                        state: {
                          projectSetup: latestProjectSetup,
                          projectSetupId,
                          resumeExistingProject: true,
                        },
                      }
                    );
                  }}
                  sx={outlineButtonSx}
                >
                  Back to Setup
                </Button>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                  {completionItems.map((item) => (
                    <StatusChip key={item.label} done={item.done} label={item.label} />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Paper>

          {projectType === "video" ? (
            <>
              <SectionCard icon={TuneIcon} title="Uploading Option" eyebrow="Ingestion plan">
                <Grid container spacing={2}>
                  {uploadChoiceOptions.map((option) => (
                    <Grid item xs={12} md={6} key={option.value}>
                      <OptionCard
                        active={uploadOptions === option.value}
                        icon={option.icon}
                        title={option.title}
                        description={option.description}
                        onClick={() => {
                          if (!projectName) {
                            toast.error("you should fill the project name first");
                            return;
                          }
                          if (option.value === "onlyVideo") {
                            setUploadedAudioTypes([
                              {
                                channelType: "right",
                                audioType: "dialogue",
                                uploadType: "demucs",
                                url: getPublicStorageUrl(`${projectName}/demucs_audios/demucs/combined_vocals.mp3`),
                              },
                              {
                                channelType: "right",
                                audioType: "music",
                                uploadType: "demucs",
                                url: getPublicStorageUrl(`${projectName}/demucs_audios/demucs/combined_no_vocals.mp3`),
                              },
                            ]);
                          } else {
                            setUploadedAudioTypes([]);
                          }

                          setUploadOption(option.value);
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </SectionCard>

              <SectionCard
                icon={PlayCircleOutlineIcon}
                title="Video Source"
                eyebrow="Required"
                action={<StatusChip done={Boolean(videoURL)} label={videoURL ? "Uploaded" : "Waiting"} />}
              >
                <Grid container spacing={2} mb={3}>
                  {videoSourceOptions.map((option) => (
                    <Grid item xs={12} sm={6} key={option.value}>
                      <OptionCard
                        active={videoOptions.chosenType === option.value}
                        icon={option.icon}
                        title={option.title}
                        onClick={() => resetVideoSource(option.value)}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: "24px",
                    border: "1px solid rgba(7,43,59,0.08)",
                    background: "rgba(248,250,252,0.74)",
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      {renderVideoSourceInput()}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={!canUploadSelectedVideoSource()}
                        startIcon={<CloudUploadIcon />}
                        onClick={handleVideoSourceUpload}
                        sx={primaryButtonSx}
                      >
                        {videoUploading ? "Uploading..." : "Upload Video Source"}
                      </Button>
                    </Grid>
                    {(videoProgress > 0 || videoUploading || videoURL) && (
                      <Grid item xs={12}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                          <Box flex={1}>
                            <LinearProgress
                              variant="determinate"
                              value={videoURL ? 100 : Math.floor(videoProgress)}
                              sx={{ height: 10, borderRadius: 99, bgcolor: "rgba(0,119,159,0.12)" }}
                            />
                          </Box>
                          <Typography fontWeight={900} color={videoURL ? "#047857" : "#00779f"}>
                            {videoURL ? "Video source uploaded successfully" : `${Math.floor(videoProgress)}%`}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </SectionCard>

              {uploadOptions === "videoAndAudio" && (
                <SectionCard
                  icon={MusicNoteIcon}
                  title="Audio Source"
                  eyebrow="Dialogue required"
                  action={<StatusChip done={uploadedAudioTypes.some((audio) => audio.audioType === "dialogue")} label={`${uploadedAudioTypes.length} uploaded`} />}
                >
                  <Stack spacing={2.5}>
                    {!showAudioAddingOption ? (
                      <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => setShowAudioAddingOption(true)}
                        sx={{ ...primaryButtonSx, alignSelf: "flex-start" }}
                      >
                        Add Audio
                      </Button>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 2, md: 2.5 },
                          borderRadius: "24px",
                          border: "1px solid rgba(0,119,159,0.12)",
                          background: "linear-gradient(135deg, rgba(0,119,159,0.07), rgba(255,255,255,0.75))",
                        }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <FieldLabel>Channel</FieldLabel>
                            <FormControl fullWidth sx={selectSx}>
                              <Select value={channelType} displayEmpty onChange={(e) => setChannelType(e.target.value)}>
                                <MenuItem value="" disabled>Choose channel</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="multi-channel">Multi-channel</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <FieldLabel>Audio type</FieldLabel>
                            <FormControl fullWidth sx={selectSx}>
                              <Select value={audioType} displayEmpty onChange={(e) => setAudioType(e.target.value)}>
                                <MenuItem value="" disabled>Choose type</MenuItem>
                                <MenuItem value="dialogue">Dialogue</MenuItem>
                                <MenuItem value="music">Music</MenuItem>
                                <MenuItem value="effect">Effect</MenuItem>
                                <MenuItem value="musicEffects">Music & Effects</MenuItem>
                                <MenuItem value="dialogueMusicEffect">Dialogue, Music, Effects</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <FieldLabel>Upload method</FieldLabel>
                            <FormControl fullWidth sx={selectSx}>
                              <Select value={uploadType} displayEmpty onChange={(e) => setUploadType(e.target.value)}>
                                <MenuItem value="" disabled>Choose method</MenuItem>
                                <MenuItem value="directUpload">Direct Upload</MenuItem>
                                <MenuItem value="googleDrive">Google Drive</MenuItem>
                                <MenuItem value="dropbox">Dropbox</MenuItem>
                                <MenuItem value="sharePoint">SharePoint</MenuItem>
                                <MenuItem value="azureBlob">Azure Blob</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                              <Button variant="outlined" onClick={() => setShowAudioAddingOption(false)} sx={outlineButtonSx}>
                                Cancel
                              </Button>
                            </Stack>
                          </Grid>
                          <Grid item xs={12}>
                            <Divider sx={{ borderColor: "rgba(7,43,59,0.08)" }} />
                          </Grid>
                          <Grid item xs={12}>
                            {renderAudioUploadInput()}
                          </Grid>
                          {audioProgress > 0 && (
                            <Grid item xs={12}>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box flex={1}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.floor(audioProgress)}
                                    sx={{ height: 10, borderRadius: 99, bgcolor: "rgba(0,119,159,0.12)" }}
                                  />
                                </Box>
                                <Typography fontWeight={900} color="#00779f">
                                  {Math.floor(audioProgress)}%
                                </Typography>
                              </Stack>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    )}

                    {uploadedAudioTypes.length > 0 ? (
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          borderRadius: "22px",
                          border: "1px solid rgba(7,43,59,0.08)",
                          overflow: "hidden",
                          bgcolor: "rgba(255,255,255,0.74)",
                        }}
                      >
                        <Table size="small">
                          <TableHead sx={{ bgcolor: "rgba(0,119,159,0.06)" }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 900 }}>Channel</TableCell>
                              <TableCell sx={{ fontWeight: 900 }}>Audio Type</TableCell>
                              <TableCell sx={{ fontWeight: 900 }}>Upload Type</TableCell>
                              <TableCell sx={{ fontWeight: 900 }}>URL</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {uploadedAudioTypes.map((audio, index) => (
                              <TableRow key={`${audio.url}-${index}`} hover>
                                <TableCell sx={{ py: 1.8 }}>
                                  <Chip label={audio.channelType} size="small" sx={{ fontWeight: 800 }} />
                                </TableCell>
                                <TableCell sx={{ py: 1.8 }}>
                                  <Chip
                                    label={audio.audioType}
                                    size="small"
                                    sx={{
                                      fontWeight: 900,
                                      color: audio.audioType === "dialogue" ? "#047857" : "#334155",
                                      bgcolor: audio.audioType === "dialogue" ? "rgba(16,185,129,0.12)" : "rgba(15,23,42,0.06)",
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 1.8 }}>{audio.uploadType}</TableCell>
                                <TableCell sx={{ py: 1.8, maxWidth: 340 }}>
                                  <Typography
                                    component="a"
                                    href={audio.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    fontSize={13}
                                    color="#00779f"
                                    sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none", fontWeight: 800 }}
                                  >
                                    {audio.url}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 1.8 }}>
                                  <IconButton
                                    onClick={() => handleRemoveUploadedFile(audio.url)}
                                    sx={{ color: "#ef4444", bgcolor: "rgba(239,68,68,0.08)", "&:hover": { bgcolor: "rgba(239,68,68,0.14)" } }}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box
                        sx={{
                          py: 5,
                          borderRadius: "22px",
                          textAlign: "center",
                          border: "1px dashed rgba(7,43,59,0.14)",
                          bgcolor: "rgba(248,250,252,0.72)",
                        }}
                      >
                        <AudiotrackIcon sx={{ color: "#94a3b8", fontSize: 38, mb: 1 }} />
                        <Typography fontWeight={900} color="#334155">
                          No audio uploaded yet
                        </Typography>
                        <Typography color="#64748b" fontSize={14}>
                          Add at least one dialogue audio before completing this project.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </SectionCard>
              )}

              <Grid container spacing={3.5} alignItems="stretch">
                <Grid item xs={12} md={7} sx={{ display: "flex", minWidth: 0 }}>
                  <SectionCard icon={SubtitlesIcon} title="Transcription Options" eyebrow="Optional subtitle seed">
                    <Stack spacing={2}>
                      {!showAddingSrtFileOption ? (
                        <Button
                          startIcon={<AddIcon />}
                          variant="outlined"
                          onClick={() => setShowAddingSrtFileOption(true)}
                          sx={{ ...outlineButtonSx, alignSelf: "flex-start" }}
                        >
                          Add SRT file
                        </Button>
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: "24px",
                            border: "1px solid rgba(0,119,159,0.12)",
                            background: "rgba(248,250,252,0.74)",
                          }}
                        >
                          <Stack spacing={2}>
                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<UploadFileIcon />}
                              sx={{ ...outlineButtonSx, alignSelf: "flex-start" }}
                            >
                              Choose SRT files
                              <input
                                hidden
                                multiple
                                type="file"
                                accept=".srt,application/x-subrip,text/plain"
                                onChange={(event) => {
                                  addSubtitleFiles(event.target.files);
                                  event.target.value = "";
                                }}
                              />
                            </Button>

                            {subtitleFiles.map((subtitle) => (
                              <Paper
                                key={subtitle.id}
                                elevation={0}
                                sx={{
                                  p: 2,
                                  borderRadius: "18px",
                                  border: "1px solid rgba(7,43,59,0.10)",
                                }}
                              >
                                <Grid container spacing={1.5} alignItems="center">
                                  <Grid item xs={12} md={4}>
                                    <Typography fontWeight={900} noWrap>
                                      {subtitle.fileName}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={5} md={3}>
                                    <FormControl fullWidth sx={selectSx}>
                                      <Select
                                        value={subtitle.role}
                                        onChange={(event) => updateSubtitleFile(
                                          subtitle.id,
                                          {
                                            role: event.target.value,
                                            languageCode:
                                              event.target.value === "original"
                                                ? ""
                                                : subtitle.languageCode,
                                          }
                                        )}
                                      >
                                        <MenuItem value="original">Original</MenuItem>
                                        <MenuItem value="translated">Translated</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} sm={5} md={4}>
                                    {subtitle.role === "translated" && (
                                      <FormControl fullWidth sx={selectSx}>
                                        <Select
                                          value={subtitle.languageCode}
                                          displayEmpty
                                          onChange={(event) => updateSubtitleFile(
                                            subtitle.id,
                                            { languageCode: event.target.value }
                                          )}
                                        >
                                          <MenuItem value="" disabled>Select language</MenuItem>
                                          {[...languages]
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map((language) => (
                                              <MenuItem key={language.code} value={language.code}>
                                                {language.name}
                                              </MenuItem>
                                            ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </Grid>
                                  <Grid item xs={12} sm={2} md={1} textAlign="right">
                                    <IconButton
                                      aria-label={`Remove ${subtitle.fileName}`}
                                      onClick={() => removeSubtitleFile(subtitle.id)}
                                      sx={{ color: "#ef4444" }}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </Grid>
                                  {subtitle.progress > 0 && subtitle.progress < 100 && (
                                    <Grid item xs={12}>
                                      <LinearProgress
                                        variant="determinate"
                                        value={subtitle.progress}
                                        sx={{ height: 8, borderRadius: 99 }}
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              </Paper>
                            ))}
                          </Stack>
                        </Paper>
                      )}
                      <Typography color="#64748b" fontSize={14}>
                        Add one original SRT for forced alignment and optional translated SRTs by language for translation mapping.
                      </Typography>
                    </Stack>
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={5} sx={{ display: "flex", minWidth: 0 }}>
                  <SectionCard icon={VisibilityIcon} title="Dubbing Options" eyebrow="Rendering behavior">
                    <Box
                      onClick={() => setFacesToggle((prev) => !prev)}
                      sx={{
                        p: 2.5,
                        borderRadius: "22px",
                        border: `1px solid ${facesToggle ? "#00779f" : "rgba(7,43,59,0.10)"}`,
                        background: facesToggle ? "rgba(0,119,159,0.08)" : "rgba(248,250,252,0.74)",
                        cursor: "pointer",
                        transition: "all 160ms ease",
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <FormControlLabel
                          onClick={(event) => event.stopPropagation()}
                          control={(
                            <Checkbox
                              checked={facesToggle}
                              onChange={(e) => setFacesToggle(e.target.checked)}
                              sx={{
                                "& .MuiSvgIcon-root": { fontSize: 28 },
                                "&.Mui-checked": { color: "#00779f" },
                              }}
                            />
                          )}
                          label=""
                          sx={{ m: 0 }}
                        />
                        <Box>
                          <Typography fontWeight={950} color="#142234">
                            Lip Sync
                          </Typography>
                          <Typography color="#64748b" fontSize={14}>
                            Run lip sync as part of the complete workflow.
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </SectionCard>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <SectionCard icon={SettingsVoiceIcon} title="Voice Selection" eyebrow="Narration voice">
                <Grid container spacing={2.5} alignItems="center">
                  <Grid item xs={12} md={7}>
                    <FieldLabel>Select Voice</FieldLabel>
                    <FormControl fullWidth sx={selectSx}>
                      <Select
                        value={selectedVoice}
                        onChange={(e) => setSelectedVoice(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>Select Voice</MenuItem>
                        <MenuItem disabled sx={{ fontWeight: 900 }}>ElevenLabs Voices</MenuItem>
                        {ELEVENLABS_VOICES.map((voice) => (
                          <MenuItem key={voice.id} value={`elevenlabs:${voice.id}`}>
                            {`${voice.name} (${voice.gender})(ElevenLabs)`}
                          </MenuItem>
                        ))}
                        <MenuItem disabled sx={{ fontWeight: 900, mt: 1 }}>Custom Model Voices</MenuItem>
                        {CUSTOM_MODEL_VOICES.map((voice) => (
                          <MenuItem key={voice.id} value={`custom:${voice.id}`}>
                            {`${voice.name} (${voice.gender})(Custom)`}
                          </MenuItem>
                        ))}
                        <MenuItem disabled sx={{ fontWeight: 900, color: "#00779f", mt: 1 }}>Special Voices</MenuItem>
                        {SPECIAL_VOICES.map((voice) => (
                          <MenuItem key={voice.id} value={`elevenlabs:${voice.id}`}>
                            {`${voice.name} (${voice.gender})(Special)`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: "22px",
                        bgcolor: selectedVoice ? "rgba(16,185,129,0.12)" : "rgba(100,116,139,0.10)",
                        border: `1px solid ${selectedVoice ? "rgba(16,185,129,0.22)" : "rgba(100,116,139,0.14)"}`,
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <GraphicEqIcon sx={{ color: selectedVoice ? "#047857" : "#64748b" }} />
                        <Box>
                          <Typography fontWeight={950} color="#142234">
                            {selectedVoice ? "Voice selected" : "No voice selected"}
                          </Typography>
                          <Typography fontSize={14} color="#64748b">
                            Choose a narration voice before finishing.
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard icon={NotesIcon} title="Upload Text" eyebrow="Audiobook source">
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={5}>
                    <Button component="label" variant="contained" startIcon={<InsertDriveFileIcon />} sx={primaryButtonSx}>
                      {txtFile || txtFileMeta ? "Change text file" : "Choose .txt file"}
                      <input hidden type="file" accept=".txt" onChange={handleTxtFileSelect} />
                    </Button>
                    <Typography mt={1.5} color="#64748b" fontSize={14}>
                      Upload a clean text file that will become the audiobook narration source.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    {(txtFile || txtFileMeta) && txtFileData ? (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: "24px",
                          border: "1px solid rgba(16,185,129,0.18)",
                          bgcolor: "rgba(16,185,129,0.08)",
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                          <CheckCircleIcon sx={{ color: "#047857" }} />
                          <Typography fontWeight={950} color="#142234">
                            Selected Text File
                          </Typography>
                        </Stack>
                        <Typography color="#334155" fontSize={14.5}>
                          File name: <strong>{txtFile?.name || txtFileMeta?.name}</strong>
                          <br />
                          Size: <strong>{(((txtFile?.size || txtFileMeta?.size || 0) / 1024).toFixed(2))} KB</strong>
                        </Typography>
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: "18px",
                            bgcolor: "rgba(255,255,255,0.78)",
                            color: "#475569",
                            fontSize: 14,
                            lineHeight: 1.6,
                          }}
                        >
                          {txtFileData.slice(0, 240)}...
                        </Box>
                      </Paper>
                    ) : (
                      <Box
                        sx={{
                          py: 5,
                          px: 2,
                          borderRadius: "22px",
                          textAlign: "center",
                          border: "1px dashed rgba(7,43,59,0.14)",
                          bgcolor: "rgba(248,250,252,0.72)",
                        }}
                      >
                        <NotesIcon sx={{ color: "#94a3b8", fontSize: 38, mb: 1 }} />
                        <Typography fontWeight={900} color="#334155">
                          No text file selected
                        </Typography>
                        <Typography color="#64748b" fontSize={14}>
                          Upload a .txt file to continue.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </SectionCard>
            </>
          )}

          <Paper
            elevation={0}
            sx={{
              ...glassCardSx,
              p: 2.5,
              position: { md: "sticky" },
              bottom: 18,
              zIndex: 5,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              minWidth={0}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap minWidth={0}>
                {completionItems.map((item) => (
                  <StatusChip key={item.label} done={item.done} label={item.label} />
                ))}
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                {projectType !== "audiobook" && (
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <Box>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={runFullWorkflow}
                            onChange={(event) => setRunFullWorkflow(event.target.checked)}
                            sx={{ color: "#00779f", "&.Mui-checked": { color: "#00779f" } }}
                          />
                        )}
                        label="Run the complete workflow"
                        sx={{ m: 0, "& .MuiFormControlLabel-label": { fontWeight: 900, color: "#142234" } }}
                      />
                      <Typography ml={4} color="#64748b" fontSize={13.5}>
                        Otherwise, stop after transcription.
                      </Typography>
                    </Box>
                    <FormControl
                      size="small"
                      sx={{ width: { xs: "100%", md: "auto" }, minWidth: { xs: 0, md: 210 } }}
                    >
                      <Select
                        value={stemSeparationProvider}
                        onChange={(event) => setStemSeparationProvider(event.target.value)}
                        aria-label="Stem separation service"
                      >
                        {STEM_SEPARATION_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography color="#64748b" fontSize={12.5} mt={0.5}>
                        Stem separation
                      </Typography>
                    </FormControl>
                  </Stack>
                )}
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<DoneAllIcon />}
                  onClick={handleSubmitCreation}
                  sx={primaryButtonSx}
                >
                  Done
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
};

export default ProjectCreationDetailsScreen;

const getVoiceName = (voiceId) => {
  return AUDIOBOOK_VOICE_NAME_MAP[voiceId] || "";
};
